import React from "react";
import { map } from "lodash";
import styled from "styled-components";
import { content } from "@glossy";
import { PeriodRange } from "@linkfluence/models/date";
import Panel from "@ui-kit/popover/panel";
import Checkboxes from "@radarly/components/filterbar/panels/checkboxes";
import { useFilterbarChoices } from "@radarly/core/filters/use-cases";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { tr } from "@radarly/i18n";
import { Clause } from "@radarly/models/clause";
import { isClauseEmpty } from "@radarly/models/clause/is";
import { refineClauseWithInnerClause } from "@radarly/models/clause/refiner";
import { transformClauseFromField } from "@radarly/models/clause/transformer";
import { FullFacet } from "@radarly/models/facets";
import {
  Choice,
  Status,
  canExcludeValues,
  getGlobalSelectors,
} from "@radarly/models/filters";
import {
  getNextClauseFromStatusAndValue,
  getNextClauseFromStatusAndValues,
} from "./clause";
import InnerPanel from "./inner-panel";

interface IProps {
  clause: Clause;
  facet: FullFacet;
  setClause: (clause: Clause) => void;
  hasTitle?: boolean;
  onApply: () => void;
  onCancel?: () => void;
  period: PeriodRange;
  validateLabel?: string;
  className?: string;
}

const Description = styled.div`
  margin-bottom: 24px;
  color: ${(props) => content(props, "secondary")};
`;

const RadarlyFilterbarPanelsCheckboxes = ({
  setClause,
  clause,
  facet,
  hasTitle,
  onApply,
  onCancel,
  period,
  validateLabel,
  className,
}: IProps) => {
  const locale = useCurrentUserLocale();
  const onStatusChange = (choice: Choice, status: Status) => {
    setClause(
      getNextClauseFromStatusAndValue(clause, facet, choice.id, status)
    );
  };

  const onGlobalStatusChange = (status: Status) => {
    setClause(
      getNextClauseFromStatusAndValues(
        clause,
        facet,
        map(choices, "id"),
        status
      )
    );
  };

  const { choices, error, loading } = useFilterbarChoices(
    clause,
    facet,
    period
  );

  const onClear = () => {
    setClause(
      refineClauseWithInnerClause(
        clause,
        transformClauseFromField(
          {
            [facet.id]: [],
          },
          facet.custom ? [facet.id] : []
        )
      )
    );
  };

  const size = choices.length < 5 ? "s" : "m";
  return (
    <Panel
      locale={locale}
      onClear={onClear}
      hasTitle={hasTitle}
      title={tr(locale, "FILTERBAR::PANEL::FILTERBY", {
        filter: facet.label,
      })}
      size={size}
      onApply={onApply}
      onCancel={onCancel}
      disabled={onCancel ? isClauseEmpty(clause) : false}
      validateLabel={validateLabel}
      className={className}
    >
      {facet.description && <Description>{facet.description}</Description>}
      <InnerPanel loading={loading} error={error} locale={locale}>
        <Checkboxes
          choices={choices}
          panelSize={size}
          allowExclusion={canExcludeValues(facet.id)}
          onStatusChange={onStatusChange}
          onGlobalStatusChange={onGlobalStatusChange}
          globalSelectors={getGlobalSelectors(facet.id, size)}
          locale={locale}
        />
      </InnerPanel>
    </Panel>
  );
};

export default RadarlyFilterbarPanelsCheckboxes;
